<template>
    <b-modal v-model="show" :title="getProductTitle()" modal-class="reg-detail-modal" size="xl" hide-footer
        no-enforce-focus>
        <div v-if="selectedProduct">
            <!-- 🔧 문제 내용 표시 영역 -->
            <div v-if="hasIssues" class="alert alert-warning mb-3" role="alert">
                <div class="d-flex align-items-start">
                    <i class="las la-exclamation-triangle text-warning mr-2" style="font-size: 1.2em; margin-top: 2px;"></i>
                    <div>
                        <strong>해결이 필요한 문제:</strong>
                        <div class="mt-2">
                            <div v-for="(issue, index) in currentIssues" :key="index" class="mb-1">
                                • {{ issue }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 선택된 옵션 정보 및 일괄 삭제 버튼 -->
            <div v-if="selectedProduct.options && selectedProduct.options.length > 0" class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <span class="text-muted">총 {{ selectedProduct.options.length }}개의 옵션</span>
                    <span v-if="selectedOptions.length > 0" class="text-primary ml-2">
                        ({{ selectedOptions.length }}개 선택됨)
                    </span>
                </div>
                <div v-if="selectedOptions.length > 0">
                    <button type="button" class="btn btn-outline-danger btn-sm" @click="deleteSelectedOptions">
                        <i class="las la-trash-alt"></i> 선택삭제 ({{ selectedOptions.length }}개)
                    </button>
                </div>
            </div>

            <CDataTable
                storage-key="RocketOptionsModal" :headers="optionHeaders" :items="selectedProduct.options" :items-per-page="10"
                :pagination="true" hover sorter itemKey="id" :show-select="true" v-model="selectedOptions">

                <!-- 옵션명 -->
                <template v-slot:optionName="{ item }">
                    <div>
                        <!-- SEO 설정이 완료되고 korName이 있는 경우 한국어 옵션명 표시 -->
                        <div v-if="isSeoCompleted && item.korName" class="font-weight-bold">
                            {{ item.korName }}
                        </div>
                        <!-- 그렇지 않으면 기존 방식으로 옵션 정보 표시 -->
                        <div v-else>
                            <div class="d-flex flex-column">
                                <div v-if="item.option1">
                                    <span class="font-weight-medium">옵션1: </span>
                                    <span>{{ item.option1.nameTrans || item.option1.name }}</span>
                                </div>
                                <div v-if="item.option2">
                                    <span class="font-weight-medium">옵션2: </span>
                                    <span>{{ item.option2.nameTrans || item.option2.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- 옵션번호 -->
                <template v-slot:optionNumber="{ item }">
                    <div class="text-center">
                        {{ item.optionNumber }}
                    </div>
                </template>

                <!-- 대표이미지(옵션이미지) 상태 -->
                <template v-slot:mainImageStatus="{ item }">
                    <div class="text-center">
                        <div class="mb-2 option-image-container">
                            <div v-if="item.mainImageStatus && (item.mainImageStatus.processedImage || item.mainImageStatus.image)"
                                class="option-image-preview">
                                <img :src="item.mainImageStatus.processedImage || item.mainImageStatus.image"
                                    @error="handleImageError" @load="handleImageLoad" class="img-fluid"
                                    :alt="`옵션 ${item.optionNumber} 이미지`">
                            </div>
                            <div v-else class="no-image-placeholder">
                                <i class="ri-image-line"></i>
                            </div>
                        </div>
                        <b-badge
                            :variant="getStatusVariant(item.mainImageStatus ? item.mainImageStatus.status : 'pending')"
                            class="badge-pill" role="button" @click="showWorkDetails(item, 'mainImage')">
                            {{ getStatusText(item.mainImageStatus ? item.mainImageStatus.status : 'pending') }}
                        </b-badge>
                    </div>
                </template>

                <!-- 추가이미지(메인이미지들) 상태 -->
                <template v-slot:additionalImageStatus="{ item }">
                    <div class="text-center">
                        <div class="mb-2 d-flex justify-content-center gap-1">
                            <template v-if="item.additionalImageStatus && item.additionalImageStatus.images && item.additionalImageStatus.images.length">
                                <img v-for="(img, idx) in item.additionalImageStatus.images.slice(0, 3)" :key="idx"
                                    :src="img" class="rounded" style="width: 30px; height: 30px; object-fit: cover;">
                                <span v-if="item.additionalImageStatus.images.length > 3">
                                    +{{ item.additionalImageStatus.images.length - 3 }}
                                </span>
                            </template>
                            <template v-else>
                                <small>이미지 없음</small>
                            </template>
                        </div>
                        <b-badge :variant="getStatusVariant(item.additionalImageStatus && item.additionalImageStatus.status ? item.additionalImageStatus.status : 'pending')" class="badge-pill"
                            role="button" @click="showWorkDetails(item, 'additionalImage')">
                            {{ getStatusText(item.additionalImageStatus && item.additionalImageStatus.status ? item.additionalImageStatus.status : 'pending') }}
                        </b-badge>
                    </div>
                </template>

                <!-- 상세이미지 상태 -->
                <template v-slot:detailImageStatus="{ item }">
                    <div class="text-center">
                        <div class="mb-2">
                            <small>{{ getDetailImageCount(item) }}개 이미지</small>
                        </div>
                        <b-badge :variant="getStatusVariant(getDetailImageStatus(item))" class="badge-pill"
                            role="button" @click="showWorkDetails(item, 'detailImage')">
                            {{ getStatusText(getDetailImageStatus(item)) }}
                        </b-badge>
                    </div>
                </template>

                <template v-slot:sizeChartStatus="{ item }">
                    <div class="text-center">
                        <b-badge
                            :variant="getStatusVariant(isSizeChartRequired(selectedProduct) ? item.sizeChartStatus : 'disabled')"
                            class="badge-pill" role="button" @click="showWorkDetails(item, 'sizeChart')">
                            {{ getStatusText(isSizeChartRequired(selectedProduct) ? item.sizeChartStatus : 'disabled')
                            }}
                        </b-badge>
                    </div>
                </template>

                <!-- 한글표시사항 상태 -->
                <template v-slot:koreanLabelStatus="{ item }">
                    <div class="text-center">
                        <b-badge :variant="getStatusVariant(item.koreanLabelStatus)" class="badge-pill" role="button"
                            @click="showWorkDetails(item, 'koreanLabel')">
                            {{ getStatusText(item.koreanLabelStatus) }}
                        </b-badge>
                    </div>
                </template>

                <!-- 가격설정 상태 -->
                <template v-slot:pricingStatus="{ item }">
                    <div class="text-center">
                        <b-badge :variant="getStatusVariant(item.pricingStatus)" class="badge-pill" role="button"
                            @click="showWorkDetails(item, 'pricing')">
                            {{ getStatusText(item.pricingStatus) }}
                        </b-badge>
                    </div>
                </template>

                <!-- SEO설정 상태 -->
                <template v-slot:seoStatus="{ item }">
                    <div class="text-center">
                        <b-badge :variant="getStatusVariant(item.seoStatus)" class="badge-pill" role="button"
                            @click="showWorkDetails(item, 'seo')">
                            {{ getStatusText(item.seoStatus) }}
                        </b-badge>
                    </div>
                </template>

                <!-- 견적서 상태 -->
                <template v-slot:quotationStatus="{ item }">
                    <div class="text-center">
                        <b-badge
                            :id="`quotation-status-${item.optionNumber}`"
                            :variant="getStatusVariant(item.quotationStatus)"
                            class="badge-pill"
                            role="button"
                            @click="showWorkDetails(item, 'quotation')">
                            {{ getStatusText(item.quotationStatus) }}
                        </b-badge>


                    </div>
                </template>

                <!-- 재고/가격 -->
                <template v-slot:stockAndPrice="{ item }">
                    <div class="text-center">
                        <div :class="{ 'text-danger': item.stock < 50 }">
                            재고: {{ item.stock }}개
                        </div>
                        <div class="mt-1">
                            <div>¥{{ item.price }} (CNY)</div>
                            <div>{{ formatNumber(item.supplyPrice) }}원 (공급가)</div>
                            <div>{{ formatNumber(item.sellingPrice) }}원 (판매가)</div>
                            <div v-if="item.bundleQuantity > 1" class="mt-1 text-primary">
                                {{ item.bundleQuantity }}개 번들구성
                            </div>
                        </div>
                    </div>
                </template>

                <!-- 관리 버튼 -->
                <template v-slot:actions="{ item }">
                    <div class="d-flex justify-content-center">
                        <b-button size="sm" variant="outline-danger" @click="deleteOption(selectedProduct, item)"
                            v-b-tooltip.hover title="옵션 삭제">
                            <i class="las la-trash-alt"></i>
                        </b-button>
                    </div>
                </template>
            </CDataTable>
        </div>
    </b-modal>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import CDataTable from '@/components/common/CDataTable';
import confirmInPageMixin from '@/mixins/confirmInPageMixin';

export default {
    mixins: [confirmInPageMixin],
    name: 'RocketOptionsModal',
    components: {
        CDataTable
    },
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedOptions: [], // 선택된 옵션들을 관리하는 배열
            optionHeaders: [
                {
                    text: '옵션번호',
                    value: 'optionNumber',
                    align: 'center',
                    sortable: true,
                    width: 120,
                    isSlot: true
                },
                {
                    text: '옵션명',
                    value: 'optionName',
                    align: 'left',
                    sortable: true,
                    width: 200, // 두 컬럼을 합쳤으므로 너비 증가
                    isSlot: true
                },
                {
                    text: 'SEO설정',
                    value: 'seoStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '가격설정',
                    value: 'pricingStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '대표이미지',
                    value: 'mainImageStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '추가이미지',
                    value: 'additionalImageStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '상세이미지',
                    value: 'detailImageStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '사이즈표',
                    value: 'sizeChartStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '한글표시사항',
                    value: 'koreanLabelStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '견적서',
                    value: 'quotationStatus',
                    align: 'center',
                    sortable: true,
                    width: 100,
                    isSlot: true
                },
                {
                    text: '재고/가격',
                    value: 'stockAndPrice',
                    align: 'center',
                    sortable: true,
                    width: 120,
                    isSlot: true
                },
                {
                    text: '관리',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 120,
                    isSlot: true
                }
            ]
        };
    },
    computed: {
        ...mapState({
            selectedProduct: state => state.selectedRocketProduct
        }),

        show: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        // SEO 설정 완료 여부 확인
        isSeoCompleted() {
            return this.selectedProduct &&
                this.selectedProduct.seoStatus === 'completed';
        },

        // 🔧 현재 문제가 있는지 확인 (견적서 작업이 시작된 경우에만)
        hasIssues() {
            // 견적서 작업이 시작되지 않은 경우 문제 표시하지 않음
            if (!this.hasQuotationStarted) {
                return false;
            }
            return this.currentIssues.length > 0;
        },

        // 🔧 견적서 작업이 시작되었는지 확인
        hasQuotationStarted() {
            if (!this.selectedProduct?.options) {
                return false;
            }

            // 하나라도 pending이 아닌 견적서 상태가 있으면 작업이 시작된 것으로 판단
            return this.selectedProduct.options.some(option =>
                option.quotationStatus && option.quotationStatus !== 'pending'
            );
        },

        // 🔧 현재 문제 목록 생성
        currentIssues() {
            if (!this.selectedProduct?.coupangDoc?.jsonDocument) {
                return [];
            }

            const issues = [];
            const allOptions = this.selectedProduct.coupangDoc.jsonDocument;

            // 1. 기본 필수 필드 검사 (각 옵션별)
            allOptions.forEach((docData, index) => {
                if (!docData.startPage?.productName) {
                    issues.push(`옵션${index + 1}: 상품명 누락`);
                }

                // 노출속성 필수 항목 검사 (스키마 기반)
                const exposedAttrs = docData.productPage?.commonAttributes?.exposedAttributes || [];
                const missingExposedAttrs = [];

                // 스키마에서 필수 노출속성 확인
                if (this.selectedProduct?.rocketRegInfo?.schemaString) {
                    try {
                        const schema = JSON.parse(this.selectedProduct.rocketRegInfo.schemaString);
                        const exposedAttrSchema = schema.properties?.productPage?.properties?.commonAttributes?.properties?.exposedAttributes;

                        if (exposedAttrSchema?.allOf) {
                            exposedAttrSchema.allOf.forEach(config => {
                                const attrConfig = config.contains?.properties?.attributeName;
                                if (attrConfig?.mandatory === true || attrConfig?.requirement === '필수') {
                                    const attrName = attrConfig.enum?.[0];
                                    if (attrName) {
                                        const hasValidValue = exposedAttrs.some(attr =>
                                            attr.attributeName === attrName &&
                                            attr.attributeValue &&
                                            attr.attributeValue.trim() !== ''
                                        );
                                        if (!hasValidValue) {
                                            missingExposedAttrs.push(attrName);
                                        }
                                    }
                                }
                            });
                        }
                    } catch (error) {
                        console.error('스키마 파싱 오류:', error);
                    }
                }

                if (missingExposedAttrs.length > 0) {
                    issues.push(`옵션${index + 1}: 필수 노출속성 누락 (${missingExposedAttrs.join(', ')})`);
                }

                // 비노출속성 검사 (설정이 활성화된 경우에만)
                const settings = this.$store.state.settings[0] || {};
                const enableUnexposedAttributes = settings.enableUnexposedAttributes !== false; // 기본값 true

                if (enableUnexposedAttributes) {
                    // 비노출속성이 활성화된 경우에만 필수 비노출속성 검사
                    const unexposedAttrs = docData.productPage?.commonAttributes?.unexposedAttributes || [];
                    const missingUnexposedAttrs = [];

                    // 스키마에서 필수 비노출속성 확인
                    if (this.selectedProduct?.rocketRegInfo?.schemaString) {
                        try {
                            const schema = JSON.parse(this.selectedProduct.rocketRegInfo.schemaString);
                            const unexposedAttrSchema = schema.properties?.productPage?.properties?.commonAttributes?.properties?.unexposedAttributes;

                            if (unexposedAttrSchema?.allOf) {
                                unexposedAttrSchema.allOf.forEach(config => {
                                    const attrConfig = config.contains?.properties?.attributeName;
                                    if (attrConfig?.mandatory === true || attrConfig?.requirement === '필수') {
                                        const attrName = attrConfig.enum?.[0];
                                        if (attrName) {
                                            const hasValidValue = unexposedAttrs.some(attr =>
                                                attr.attributeName === attrName &&
                                                attr.attributeValue &&
                                                attr.attributeValue.trim() !== ''
                                            );
                                            if (!hasValidValue) {
                                                missingUnexposedAttrs.push(attrName);
                                            }
                                        }
                                    }
                                });
                            }
                        } catch (error) {
                            console.error('비노출속성 스키마 파싱 오류:', error);
                        }
                    }

                    if (missingUnexposedAttrs.length > 0) {
                        issues.push(`옵션${index + 1}: 필수 비노출속성 누락 (${missingUnexposedAttrs.join(', ')})`);
                    }
                }
                // 비노출속성이 비활성화된 경우 관련 오류 표시하지 않음

                // 가격 필드 검증
                const purchasePrice = docData.productPage?.commonAttributes?.purchasePrice;
                const coupangSalePrice = docData.productPage?.commonAttributes?.coupangSalePrice;

                if (!purchasePrice || purchasePrice <= 0) {
                    issues.push(`옵션${index + 1}: 공급가 누락`);
                }
                if (!coupangSalePrice || coupangSalePrice <= 0) {
                    issues.push(`옵션${index + 1}: 판매가 누락`);
                }
            });

            // 2. 노출속성 중복 검사
            const duplicateResult = this.checkExposedAttributesDuplication(allOptions);
            if (!duplicateResult.isValid) {
                issues.push(duplicateResult.reason);
            }

            return issues;
        }
    },
    methods: {
        ...mapMutations([
            'SET_SELECTED_OPTION',
            'SET_SELECTED_WORK_TYPE'
        ]),
        isSizeChartRequired(product) {
            try {
                if (product && product.rocketRegInfo && product.rocketRegInfo.schemaString) {
                    const schema = JSON.parse(product.rocketRegInfo.schemaString);

                    if (schema.properties &&
                        schema.properties.imagePage &&
                        schema.properties.imagePage.properties &&
                        schema.properties.imagePage.properties.images &&
                        schema.properties.imagePage.properties.images.properties &&
                        schema.properties.imagePage.properties.images.required) {

                        return schema.properties.imagePage.properties.images.required.includes('sizeChartImage');
                    }
                }
            } catch (error) {
                console.error('스키마 파싱 오류:', error);
            }
            return false;
        },

        // 상태 표시 관련 메서드
        getStatusVariant(status) {
            switch (status) {
                case 'completed':
                    return 'success';
                case 'partially_completed':
                    return 'info';
                case 'processing':
                    return 'primary';
                case 'pending':
                    return 'secondary';
                case 'error':
                    return 'danger';
                case 'disabled':
                    return 'light';
                default:
                    return 'info';
            }
        },

        getStatusText(status) {
            const texts = {
                completed: '완료',
                partially_completed: '일부완료',
                processing: '진행중',
                pending: '대기',
                error: '오류',
                disabled: '미사용'
            };
            return texts[status] || '대기';
        },



        // 🔧 노출속성 중복 검사
        checkExposedAttributesDuplication(jsonDocumentData) {
            const exposedAttributesMap = new Map();
            const duplicateGroups = [];

            jsonDocumentData.forEach((doc, index) => {
                if (doc?.productPage?.commonAttributes?.exposedAttributes) {
                    const exposedAttrs = doc.productPage.commonAttributes.exposedAttributes;

                    // 속성명과 속성값을 모두 포함한 키 생성
                    const attrKey = JSON.stringify(exposedAttrs.map(attr => ({
                        name: attr.attributeName,
                        value: attr.attributeValue
                    })).sort((a, b) => a.name.localeCompare(b.name)));

                    if (exposedAttributesMap.has(attrKey)) {
                        // 중복 발견
                        const existingIndex = exposedAttributesMap.get(attrKey);
                        let duplicateGroup = duplicateGroups.find(group => group.includes(existingIndex));
                        if (!duplicateGroup) {
                            duplicateGroup = [existingIndex];
                            duplicateGroups.push(duplicateGroup);
                        }
                        duplicateGroup.push(index);
                    } else {
                        exposedAttributesMap.set(attrKey, index);
                    }
                }
            });

            if (duplicateGroups.length > 0) {
                // 중복된 속성값들 추출
                const duplicateValues = [];
                duplicateGroups.forEach(group => {
                    const firstDoc = jsonDocumentData[group[0]];
                    if (firstDoc?.productPage?.commonAttributes?.exposedAttributes) {
                        const attrs = firstDoc.productPage.commonAttributes.exposedAttributes;
                        attrs.forEach(attr => {
                            if (attr.attributeValue && !duplicateValues.includes(attr.attributeValue)) {
                                duplicateValues.push(attr.attributeValue);
                            }
                        });
                    }
                });

                const duplicateInfo = duplicateGroups.map((group) =>
                    `옵션 ${group.map(idx => idx + 1).join(', ')}`
                ).join(', ');

                return {
                    isValid: false,
                    reason: `노출속성 중복: "${duplicateValues.join(', ')}" (${duplicateInfo})`
                };
            }

            return { isValid: true };
        },

        // 작업 상세 보기 및 위자드 열기
        showWorkDetails(option, workType) {
            // 선택된 옵션과 작업 유형 저장
            this.SET_SELECTED_OPTION(option);
            this.SET_SELECTED_WORK_TYPE(workType);
            console.log('option클릭', option)
            console.log(this.selectedProduct)
            // 작업 위자드 모달 열기 이벤트 발생
            // this.$nextTick(() => {
            this.$emit('open-wizard');
            // });
        },

        // 선택된 옵션들 일괄 삭제
        async deleteSelectedOptions() {
            if (this.selectedOptions.length === 0) {
                this.$bvToast.toast('삭제할 옵션을 선택해주세요.', {
                    title: '알림',
                    variant: 'warning',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
                return;
            }

            const optionCount = this.selectedOptions.length;
            const hasAnyCoupangDoc = this.selectedOptions.some(option => option.coupangDoc && option.coupangDoc.docId);

            if (await this.confirmInPage(`선택된 ${optionCount}개의 옵션${hasAnyCoupangDoc ? '과 관련 쿠팡 문서' : ''}를 삭제하시겠습니까?`)) {
                // 쿠팡 문서가 있는 옵션들의 문서 삭제 요청
                this.selectedOptions.forEach(option => {
                    if (option.coupangDoc && option.coupangDoc.docId) {
                        window.postMessage({
                            type: 'DELETE_ROCKET_REG_DOC',
                            id: `delete_option_${option.optionNumber}_${Date.now()}`,
                            data: {
                                docId: option.coupangDoc.docId,
                                suppliercode: this.$store.state.user.suppliercode
                            }
                        }, '*');

                        console.log(`옵션 ${option.optionNumber}의 쿠팡 문서 삭제 요청 (docId: ${option.coupangDoc.docId})`);
                    }
                });

                // 선택된 옵션들의 ID 목록 생성
                const selectedOptionIds = this.selectedOptions.map(option => option.id);

                // 옵션 목록에서 선택된 옵션들 제거
                const updatedOptions = this.selectedProduct.options.filter(option => !selectedOptionIds.includes(option.id));

                // 상품 업데이트 객체 생성
                const updatedProduct = {
                    ...this.selectedProduct,
                    options: updatedOptions
                };

                // product.coupangDoc.jsonDocument 배열에서도 해당 인덱스들 제거
                if (updatedProduct.coupangDoc &&
                    updatedProduct.coupangDoc.jsonDocument &&
                    Array.isArray(updatedProduct.coupangDoc.jsonDocument)) {

                    console.log(`coupangDoc.jsonDocument 배열에서 ${optionCount}개 옵션 제거 전 길이: ${updatedProduct.coupangDoc.jsonDocument.length}`);

                    // 제거할 인덱스들을 역순으로 정렬하여 제거 (인덱스 변화 방지)
                    const indicesToRemove = [];
                    this.selectedOptions.forEach(selectedOption => {
                        const index = this.selectedProduct.options.findIndex(option => option.id === selectedOption.id);
                        if (index !== -1) {
                            indicesToRemove.push(index);
                        }
                    });

                    // 인덱스를 역순으로 정렬하여 제거
                    indicesToRemove.sort((a, b) => b - a);
                    const updatedJsonDocument = [...updatedProduct.coupangDoc.jsonDocument];
                    indicesToRemove.forEach(index => {
                        if (index < updatedJsonDocument.length) {
                            updatedJsonDocument.splice(index, 1);
                        }
                    });
                    updatedProduct.coupangDoc.jsonDocument = updatedJsonDocument;

                    console.log(`coupangDoc.jsonDocument 배열에서 ${optionCount}개 옵션 제거 후 길이: ${updatedProduct.coupangDoc.jsonDocument.length}`);
                } else {
                    console.warn('coupangDoc.jsonDocument 배열을 찾을 수 없습니다.');
                    if (updatedProduct.coupangDoc) {
                        console.log('coupangDoc 구조:', JSON.stringify(updatedProduct.coupangDoc).substring(0, 100) + '...');
                    }
                }

                // pricing.options 배열에서도 삭제된 옵션들 제거
                if (updatedProduct.pricing && updatedProduct.pricing.options && Array.isArray(updatedProduct.pricing.options)) {
                    console.log(`🔄 pricing.options 배열에서 ${optionCount}개 옵션 제거 전 길이: ${updatedProduct.pricing.options.length}`);
                    console.log('🔄 제거 전 pricing.options:', updatedProduct.pricing.options.map(opt => opt.optionNumber));

                    // 삭제된 옵션의 optionNumber 목록 생성
                    const deletedOptionNumbers = this.selectedOptions.map(option => option.optionNumber);
                    console.log('🗑️ 삭제할 옵션 번호들:', deletedOptionNumbers);

                    // pricing.options에서 삭제된 옵션들 제거
                    updatedProduct.pricing.options = updatedProduct.pricing.options.filter(
                        pricingOption => !deletedOptionNumbers.includes(pricingOption.optionNumber)
                    );

                    console.log(`✅ pricing.options 배열에서 ${optionCount}개 옵션 제거 후 길이: ${updatedProduct.pricing.options.length}`);
                    console.log('✅ 제거 후 pricing.options:', updatedProduct.pricing.options.map(opt => opt.optionNumber));
                } else {
                    console.log('⚠️ pricing.options가 존재하지 않음');
                }

                // 선택 목록 초기화
                this.selectedOptions = [];

                // 상품 업데이트 처리 이벤트 발생
                this.$emit('option-deleted', updatedProduct);

                // 삭제 완료 메시지
                this.$bvToast.toast(`${optionCount}개의 옵션이 삭제되었습니다.`, {
                    title: '삭제 완료',
                    variant: 'success',
                    solid: true,
                    toaster: 'b-toaster-bottom-right'
                });
            }
        },

        // 옵션 삭제
        async deleteOption(product, option) {
            if (!option || !product) {
                console.error('삭제할 옵션 또는 상품 정보가 없습니다.');
                return;
            }

            // 옵션에 쿠팡 문서 ID가 있는지 확인
            const hasCoupangDoc = option.coupangDoc && option.coupangDoc.docId;

            if (await this.confirmInPage(`이 옵션${hasCoupangDoc ? '과 관련 쿠팡 문서' : ''}를 삭제하시겠습니까?`)) {
                // 쿠팡 문서가 있는 경우 확장 프로그램에 삭제 요청
                if (hasCoupangDoc) {
                    window.postMessage({
                        type: 'DELETE_ROCKET_REG_DOC',
                        id: `delete_option_${option.optionNumber}_${Date.now()}`,
                        data: {
                            docId: option.coupangDoc.docId,
                            suppliercode: this.$store.state.user.suppliercode
                        }
                    }, '*');

                    console.log(`옵션 ${option.optionNumber}의 쿠팡 문서 삭제 요청 (docId: ${option.coupangDoc.docId})`);
                }

                // 옵션 목록에서 삭제
                const index = product.options.findIndex(o => o.id === option.id);
                if (index !== -1) {
                    const updatedOptions = [...product.options];
                    updatedOptions.splice(index, 1);

                    // 상품 업데이트 객체 생성
                    const updatedProduct = {
                        ...product,
                        options: updatedOptions
                    };

                    // product.coupangDoc.jsonDocument 배열에서도 해당 인덱스 제거
                    if (updatedProduct.coupangDoc &&
                        updatedProduct.coupangDoc.jsonDocument &&
                        Array.isArray(updatedProduct.coupangDoc.jsonDocument) &&
                        updatedProduct.coupangDoc.jsonDocument.length > index) {

                        console.log(`coupangDoc.jsonDocument 배열에서 인덱스 ${index} 제거 전 길이: ${updatedProduct.coupangDoc.jsonDocument.length}`);

                        // 깊은 복사를 통해 새 배열 생성
                        const updatedJsonDocument = [...updatedProduct.coupangDoc.jsonDocument];
                        updatedJsonDocument.splice(index, 1);
                        updatedProduct.coupangDoc.jsonDocument = updatedJsonDocument;

                        console.log(`coupangDoc.jsonDocument 배열에서 인덱스 ${index} 제거 후 길이: ${updatedProduct.coupangDoc.jsonDocument.length}`);
                    } else {
                        console.warn('coupangDoc.jsonDocument 배열을 찾을 수 없거나 인덱스가 범위를 벗어났습니다.');
                        if (updatedProduct.coupangDoc) {
                            console.log('coupangDoc 구조:', JSON.stringify(updatedProduct.coupangDoc).substring(0, 100) + '...');
                        }
                    }

                    // pricing.options 배열에서도 삭제된 옵션 제거
                    if (updatedProduct.pricing && updatedProduct.pricing.options && Array.isArray(updatedProduct.pricing.options)) {
                        console.log(`🔄 pricing.options 배열에서 옵션 ${option.optionNumber} 제거 전 길이: ${updatedProduct.pricing.options.length}`);
                        console.log('🔄 제거 전 pricing.options:', updatedProduct.pricing.options.map(opt => opt.optionNumber));

                        // pricing.options에서 삭제된 옵션 제거
                        updatedProduct.pricing.options = updatedProduct.pricing.options.filter(
                            pricingOption => pricingOption.optionNumber !== option.optionNumber
                        );

                        console.log(`✅ pricing.options 배열에서 옵션 ${option.optionNumber} 제거 후 길이: ${updatedProduct.pricing.options.length}`);
                        console.log('✅ 제거 후 pricing.options:', updatedProduct.pricing.options.map(opt => opt.optionNumber));
                    } else {
                        console.log('⚠️ pricing.options가 존재하지 않음');
                    }

                    // 상품 업데이트 처리 이벤트 발생
                    this.$emit('option-deleted', updatedProduct);

                    // 삭제 완료 메시지
                    this.$bvToast.toast(`옵션 ${option.optionNumber}이(가) 삭제되었습니다.`, {
                        title: '삭제 완료',
                        variant: 'success',
                        solid: true,
                        toaster: 'b-toaster-bottom-right'
                    });
                }
            }
        },

        // 이미지 관련 메서드
        handleImageError() {
            console.warn('이미지 로드 실패');
        },

        handleImageLoad() {
            // 이미지 로드 성공 시 필요한 처리
        },

        // 숫자 포맷팅
        formatNumber(value) {
            return new Intl.NumberFormat().format(value);
        },

        // 상세 이미지 상태 처리 (문자열 또는 객체 형식 모두 지원)
        getDetailImageStatus(item) {
            // detailImageStatus가 문자열인 경우 (예: 'completed', 'error', 'pending')
            if (typeof item.detailImageStatus === 'string') {
                return item.detailImageStatus;
            }

            // detailImageStatus가 객체인 경우 (예: {status: 'completed', images: [...]})
            if (item.detailImageStatus && item.detailImageStatus.status) {
                return item.detailImageStatus.status;
            }

            // 둘 다 아닌 경우 기본값
            return 'pending';
        },

        // 상세 이미지 개수 구하기 (문자열 또는 객체 형식 모두 지원)
        getDetailImageCount(item) {
            // detailImageStatus가 객체이고 images 배열이 있는 경우
            if (item.detailImageStatus && item.detailImageStatus.images && Array.isArray(item.detailImageStatus.images)) {
                return item.detailImageStatus.images.length;
            }

            // detailImageStatusObj가 있고 images 배열이 있는 경우
            if (item.detailImageStatusObj && item.detailImageStatusObj.images && Array.isArray(item.detailImageStatusObj.images)) {
                return item.detailImageStatusObj.images.length;
            }

            // 직접 detailImages 배열이 있는 경우
            if (item.detailImages && Array.isArray(item.detailImages)) {
                return item.detailImages.length;
            }

            // 이미지 정보를 찾을 수 없는 경우
            return 0;
        },

        getProductTitle() {
            if (this.selectedProduct) {
                // SEO 설정이 완료된 경우 SEO 상품명 사용
                if (this.selectedProduct.seoStatus === 'completed' && this.selectedProduct.seo && this.selectedProduct.seo.productName) {
                    return this.selectedProduct.seo.productName;
                }
                // 아니면 기존 상품명 사용
                return this.selectedProduct.productName || this.selectedProduct.productNameTrans;
            }
            return '옵션 정보';
        }
    },
    watch: {
        // 선택된 상품이 변경되면 선택 목록 초기화 (단, registrationId가 다를 때만)
        selectedProduct: {
            handler(newProduct, oldProduct) {
                // 상품이 완전히 바뀐 경우에만 선택 목록 초기화
                if (!oldProduct || !newProduct ||
                    oldProduct.registrationId !== newProduct.registrationId) {
                    this.selectedOptions = [];
                    console.log('🔄 상품 변경으로 인한 선택 목록 초기화');
                } else {
                    // 같은 상품의 업데이트인 경우, 삭제된 옵션만 선택 목록에서 제거
                    if (newProduct.options && this.selectedOptions.length > 0) {
                        const currentOptionIds = newProduct.options.map(opt => opt.id);
                        const validSelectedOptions = this.selectedOptions.filter(
                            selectedOpt => currentOptionIds.includes(selectedOpt.id)
                        );

                        if (validSelectedOptions.length !== this.selectedOptions.length) {
                            this.selectedOptions = validSelectedOptions;
                            console.log('🔄 삭제된 옵션을 선택 목록에서 제거');
                        }
                    }
                }
            },
            deep: true
        },
        // 모달이 열릴 때 선택 목록 초기화
        show(newValue) {
            if (newValue) {
                this.selectedOptions = [];
                console.log('🔄 모달 열림으로 인한 선택 목록 초기화');
            }
        }
    }
};
</script>

<style scoped>
::v-deep .reg-detail-modal .modal-dialog {
    max-width: 95%;
    max-height: 100%;
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
}
.option-image-preview {
    position: relative;
    width: 50px;
    height: 50px;
    margin: 0 auto;
    overflow: hidden;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s;
}

.option-image-preview:hover {
    transform: scale(1.1);
    z-index: 1;
}

.no-image-placeholder {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fa;
    border: 1px dashed #dee2e6;
    border-radius: 4px;
    margin: 0 auto;
}

.no-image-placeholder i {
    font-size: 24px;
    color: #adb5bd;
}
</style>